// store/mainChat/actions.js

import { apiRequest } from "@/services";
import * as env from "@/config/env";

const REQUEST_CACHE_TTL = 15000;
const requestCache = new Map();
const inFlightRequests = new Map();

// ============ HELPER FUNCTIONS ============

function getCachedRequest(key) {
    const cached = requestCache.get(key);
    if (!cached) return null;

    if (Date.now() - cached.time > REQUEST_CACHE_TTL) {
        requestCache.delete(key);
        return null;
    }

    return cached.data;
}

function setCachedRequest(key, data) {
    requestCache.set(key, {
        data,
        time: Date.now(),
    });
}

function withRequestCache(key, fetcher) {
    const cached = getCachedRequest(key);
    if (cached) return Promise.resolve(cached);

    if (inFlightRequests.has(key)) {
        return inFlightRequests.get(key);
    }

    const request = Promise.resolve()
        .then(fetcher)
        .then((data) => {
            setCachedRequest(key, data);
            return data;
        })
        .finally(() => {
            inFlightRequests.delete(key);
        });

    inFlightRequests.set(key, request);
    return request;
}

function detectChatType(conversation) {
    if (conversation.chatType) return conversation.chatType;
    if (conversation.isGroupChat || conversation.isGroup) return "groupChat";
    const members = conversation.AssigneeUserId || conversation.members || [];
    return members.length > 2 ? "groupChat" : "oneToOne";
}

function normalizeConversation(doc) {
    const id = doc._id || doc.id;
    const chatType = doc.chatType || detectChatType(doc);

    return {
        ...doc,
        id,
        _id: id,
        chatType,
        isGroupChat: chatType === "groupChat",
        isGroup: chatType === "groupChat",
        name: doc.name || doc.TaskName || doc.GroupName,
        TaskName: doc.name || doc.TaskName || doc.GroupName,
        lastMessage: doc.lastMessage || doc.lastMessageTime || doc.lastMessageObj?.createdAt,
    };
}

// ============ UNIFIED CONVERSATIONS ACTION ============

export const setConversations = async ({ commit, state }, payload) => {
    return new Promise((resolve, reject) => {
        try {
            commit("setConversationPayload", payload);
            commit("setConversationsLoading", true);

            if (!payload.from || payload.from !== "storeWatch") {

                const params = new URLSearchParams({
                    companyId: payload.companyId,
                    userId: payload.userId,
                });

                // Optional: filter by chatType
                if (payload.chatType) {
                    params.append("chatType", payload.chatType);
                }

                // Optional: search
                if (payload.search && payload.search.trim()) {
                    params.append("search", payload.search.trim());
                }

                const url = `${env.CONVERSATIONS}?${params.toString()}`;
                const cacheKey = `conversations:${params.toString()}`;

                withRequestCache(cacheKey, () => apiRequest("get", url).then((res) => {
                    const raw = res?.data?.data || [];
                    return raw.map((conversation) => normalizeConversation(conversation));
                }))
                    .then((list) => {
                        commit("resetConversations", list);
                        commit("setConversationsLoading", false);
                        resolve(list);
                    })
                    .catch((error) => {
                        console.error("[setConversations] API error:", error);
                        commit("setConversationsLoading", false);
                        commit("resetConversations", []);
                        reject(error);
                    });
            } else {

                commit("setConversationsLoading", false);
                resolve(state.conversations?.data || []);
            }
        } catch (e) {
            commit("setConversationsLoading", false);
            reject(e);
        }
    });
};

// Sync conversations khi tab visible
export const setConversationTabSync = ({ state, commit }, payload) => {
    const freshList = payload.response || [];
    const oldList = state.conversations?.data || [];

    const ops = [];

    freshList.forEach((conversation) => {
        const normalized = normalizeConversation(conversation);

        const idx = oldList.findIndex((x) => String(x._id) === String(normalized._id));

        if (idx === -1) {
            ops.push({ op: "added", data: normalized });
        } else if (JSON.stringify(oldList[idx]) !== JSON.stringify(normalized)) {
            ops.push({ op: "modified", data: normalized });
        }
    });

    // Check for removed items
    oldList.forEach((oldItem) => {
        const stillExists = freshList.some((x) => String(x._id) === String(oldItem._id));
        if (!stillExists) {
            ops.push({ op: "removed", data: { _id: oldItem._id, id: oldItem._id } });
        }
    });

    if (ops.length) {
        commit("mutateConversations", ops);
    }
};

// ============ LEGACY CHATS ACTION ============

export const setChatTabSync = ({ state, commit }, payload) => {
    const { response } = payload;
    response.forEach((ele) => {
        const index = state.chats.data.findIndex((element) => element._id === ele._id);
        const op = index !== -1 ? "modified" : "added";

        commit("mutateChats", [{ op, data: ele }]);
        commit("mutateConversations", [
            {
                op,
                data: normalizeConversation({ ...ele, chatType: "oneToOne" }),
            },
        ]);
    });
};

// ============ LEGACY GROUP CHATS ACTION ============

export const setGroupChats = async ({ commit, state }, payload) => {
    return new Promise((resolve, reject) => {
        try {
            commit("setGroupChatPayload", payload);            
            if (!payload.from && payload.from !== "storeWatch") {
                const params = new URLSearchParams({
                    companyId: payload.companyId,
                    userId: payload.userId,
                    chatType: "groupChat",
                });
                
                if (payload.search && payload.search.trim()) {
                    params.append("search", payload.search.trim());
                }

                const url = `${env.CONVERSATIONS}?${params.toString()}`;

                apiRequest("get", url)
                    .then((res) => {
                        const raw = res?.data?.data || [];
                        const list = raw.map((groupChat) => ({
                            ...groupChat,
                            isGroupChat: true,
                        }));

                        commit("resetGroupChats", list);

                        // Also add to unified conversations store
                        const conversationOps = list.map((chat) => ({
                            op: "added",
                            data: normalizeConversation({ ...chat, chatType: "groupChat" }),
                        }));
                        commit("mutateConversations", conversationOps);

                        resolve(list);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } else {
                resolve(state.groupChats?.data || []);
            }
        } catch (e) {
            reject(e);
        }
    });
};

export const setGroupChatTabSync = ({ state, commit }, payload) => {
    const freshList = payload.response || [];
    const oldList = state.groupChats.data || [];

    const ops = [];
    const conversationOps = [];

    freshList.forEach((conversation) => {
        const idx = oldList.findIndex((x) => x._id === conversation._id);
        const op = idx === -1 ? "added" : "modified";

        ops.push({ op, data: conversation });
        conversationOps.push({
            op,
            data: normalizeConversation({ ...conversation}),
        });
    });

    if (ops.length) {
        commit("mutateGroupChats", ops);
        commit("mutateConversations", conversationOps);
    }
};

// ============ PROJECTS, SPRINTS, FOLDERS ACTIONS ============

export const setChatProjects = ({ commit }) => {
    return new Promise((resolve, reject) => {
        try {
            withRequestCache("chatProjects", () => apiRequest("get", env.MAIN_CHATS).then((chats) => chats.data || []))
                .then((projects) => {
                    projects.forEach((ch) => {
                        commit("mutateChatProjects", [
                            {
                                op: "added",
                                data: { ...ch, _id: ch._id, sprintsObj: {}, sprintsfolders: {} },
                            },
                        ]);
                    });
                    resolve(projects);
                })
                .catch((error) => {
                    reject(error);
                });
        } catch (error) {
            reject(error);
        }
    });
};

export const setChatSprints = ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
        try {
            const projectId = payload?.projectId;
            if (!projectId) {
                resolve([]);
                return;
            }

            const cacheKey = `chatSprints:${projectId}`;
            const url = `/api/v1/${env.GET_SPRINT_OR_PROJECT}/${projectId}?collection=sprints`;

            withRequestCache(cacheKey, () => apiRequest("get", url).then((resp) => resp?.data || []))
                .then((res) => {
                    res.forEach((x) => {
                        commit("mutateChatSprints", { op: "added", data: { ...x } });
                    });
                    resolve(res);
                })
                .catch((error) => {
                    reject(error);
                });
        } catch (error) {
            reject(error);
        }
    });
};

export const setChatFolders = ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
        try {
            const projectId = payload?.projectId;
            if (!projectId) {
                resolve([]);
                return;
            }

            const cacheKey = `chatFolders:${projectId}`;
            const url = `/api/v1/${env.GET_SPRINT_OR_PROJECT}/${projectId}?collection=folders`;

            withRequestCache(cacheKey, () => apiRequest("get", url).then((resp) => resp?.data || []))
                .then((res) => {
                    res.forEach((x) => {
                        commit("mutateChatFolders", { op: "added", data: { ...x } });
                    });
                    resolve(res);
                })
                .catch((error) => {
                    reject(error);
                });
        } catch (error) {
            reject(error);
        }
    });
};
